import { Button, ColumnProps, Input, Select, Modal, Space, Switch, Table, Tag, message } from 'gokwik-ui-kit'
import { ComponentProps, PropsWithRef, useEffect, useRef, useState } from 'react'
import { ArrowDownOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { getUserDetails } from '@store/user/selectors'
import { downloadToCSV } from '@gokwik/utilities'

interface Merchant {
    label: string
    value: string
    enabled: boolean
    original: any
}

interface MerchantSelectorProps {
    value?: Record<string, boolean>
    onChange?: (value: Record<string, boolean>) => void
    isEdit?: boolean
    id?: string
}
const tokenSeparators = [',', '\n', '\r\n']
const selectClass =
    'h-40 max-h-40 [&>.ant-select-selector]:overflow-y-auto [&>.ant-select-selector]:items-start [&>.ant-select-selector]:p-2'
const filterOption = (input, option) =>
    option.label?.toString().toLocaleLowerCase().indexOf(input.toLocaleLowerCase()) >= 0 ||
    option.value?.toString().toLocaleLowerCase().indexOf(input.toLocaleLowerCase()) >= 0

const MerchantSelector = ({ value = {}, onChange, isEdit, id }: MerchantSelectorProps) => {
    const [searchText, setSearchText] = useState('')
    const [bulkOpen, setBulkOpen] = useState<'add' | 'remove' | null>(null)
    const [bulkIds, setBulkIds] = useState([])
    const [searchedColumn, setSearchedColumn] = useState('')
    const searchInput = useRef(null)
    const sortHeaderRef = useRef(null)
    const sortHeaderDir = useRef(null)
    const userData = useSelector(getUserDetails)
    const [merchants, setMerchants] = useState<Merchant[]>([])
    const [downloadMerchantList, setDownloadMerchantList] = useState([])
    const [pagination, setPagination] = useState<ComponentProps<typeof Table>['pagination']>({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: false,
    })

    const handleSearch = (selectedKeys: string[], confirm, dataIndex) => {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
    }

    const handleReset = (clearFilters: () => void, confirm) => {
        clearFilters()
        setSearchText('')
        confirm()
    }

    const getColumnSearchProps: (index, name) => ColumnProps<any> = (dataIndex, name) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${name}`}
                    value={selectedKeys[0] as string}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type='primary'
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size='small'
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        size='small'
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100)
                }
            },
        },
    })

    useEffect(() => {
        const downloadList = []
        setMerchants(
            userData?.allowed_merchants?.map((row) => {
                if (value[row.merchant_id]) {
                    downloadList.push({ label: row.short_name ?? row.business_name, value: row.merchant_id })
                }
                return {
                    label: row.short_name ?? row.business_name,
                    value: row.merchant_id,
                    enabled: value[row.merchant_id] || false,
                    original: row,
                }
            }),
        )
        setDownloadMerchantList(downloadList)
        setPagination((prev) => ({ ...prev, total: userData?.allowed_merchants?.length }))
    }, [userData])

    const handleToggle = (merchantId: string, checked: boolean) => {
        const newValue = { ...value, [merchantId]: checked }
        onChange?.(newValue)
    }

    const handleTableChange = (newPagination: any, _, sorter) => {
        sortHeaderDir.current = sorter.order
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current || 1,
            pageSize: newPagination.pageSize || 10,
        }))
    }

    const downloadMerchants = () => {
        downloadToCSV(['label', 'value'], ['Merchant Name', 'Merchant ID'], downloadMerchantList, `merchant-list-${id}`)
    }

    const columns: ColumnProps<any>[] = [
        {
            title: 'Merchant Name',
            dataIndex: 'label',
            key: 'label',
            width: '40%',
            ...getColumnSearchProps('label', 'Merchant Name'),
        },
        {
            title: 'Merchant ID',
            dataIndex: 'value',
            key: 'value',
            width: '40%',
            ...getColumnSearchProps('value', 'Merchant ID'),
        },
        {
            title: <p ref={sortHeaderRef}>Status</p>,
            dataIndex: 'value',
            key: 'status',
            width: '20%',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => +(value[b.value] ?? false) - +(value[a.value] ?? false),
            render: (merchantId: string) => (
                <div className='flex items-center gap-x-2'>
                    <Switch
                        checked={value[merchantId] ?? false}
                        onChange={(checked) => handleToggle(merchantId, checked)}
                    />
                </div>
            ),
        },
    ]

    useEffect(() => {
        if (!bulkOpen) setBulkIds([])
    }, [bulkOpen])

    const handleBulk = () => {
        let newValue = { ...value }
        bulkIds?.forEach((mid) => {
            newValue = { ...newValue, [mid]: bulkOpen === 'add' }
        })
        onChange?.(newValue)
        setBulkOpen(null)
        sortCol(0)
        message.info('Please update the offer for the changes to reflect')
    }

    const sortCol = (i) => {
        if (i > 5) return
        sortHeaderRef.current?.click()
        if (sortHeaderDir.current !== 'ascend') {
            setTimeout(() => {
                sortCol(i + 1)
            }, 10)
        }
    }

    return (
        <>
            <div className='flex flex-col gap-y-4'>
                <div className='flex justify-end items-center gap-x-2'>
                    {isEdit && (
                        <Button onClick={downloadMerchants} size='small' icon={<DownloadOutlined />}>
                            Added Merchants
                        </Button>
                    )}
                    <Button type='primary' size='small' onClick={() => setBulkOpen('add')}>
                        Bulk Add
                    </Button>
                    <Button type='primary' size='small' onClick={() => setBulkOpen('remove')}>
                        Bulk Remove
                    </Button>
                </div>
                <Table
                    className='[&_.ant-spin-container>ul:not(:last-child)]:hidden'
                    columns={columns}
                    dataSource={merchants}
                    rowKey='value'
                    pagination={searchText?.length > 2 ? false : pagination}
                    onChange={handleTableChange}
                />
            </div>
            <Modal
                title={<span className='text-lg font-semibold capitalize'>{bulkOpen} Merchants</span>}
                open={!!bulkOpen}
                onCancel={() => setBulkOpen(null)}
                onOk={handleBulk}
                centered
                width={'450px'}
                okText={<span className='capitalize'>{bulkOpen}</span>}
            >
                <div className='flex flex-col'>
                    <p className='font-medium'>Search using Merchant Name or ID</p>
                    <p className='text-xs text-gray-600'>Or paste in comma-separated/column data (max count 500)</p>
                    <Select
                        allowClear
                        mode='multiple'
                        rootClassName='mt-2'
                        style={{ width: '100%' }}
                        tokenSeparators={tokenSeparators}
                        filterOption={filterOption}
                        className={selectClass}
                        onChange={(vals) => {
                            setBulkIds(vals)
                        }}
                        optionFilterProp='value'
                        value={bulkIds}
                        maxCount={500}
                        options={merchants}
                        optionRender={(option) => <OptionRender option={option} />}
                    />
                </div>
            </Modal>
        </>
    )
}

function OptionRender({ option }: { option: Parameters<PropsWithRef<Select>['defaultProps']['optionRender']>['0'] }) {
    return (
        <div className='flex items-center justify-between gap-x-3'>
            <div>
                <p className='text-primary-500 font-medium'>{option.label}</p>
                <p className='text-xs text-gray-500'>{option.value}</p>
            </div>
            {option.data.original?.platform && (
                <Tag className='capitalize' variant='warning'>
                    {option.data.original?.platform}
                </Tag>
            )}
        </div>
    )
}

export default MerchantSelector
